import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronRight, Search, UserPlus } from "lucide-react";
import { cancelInvite, cancelRequest, getRoster } from "../../../services/CareTeamService";
import {
  cancelLabel,
  filterRoster,
  formatDate,
  isFlagged,
  lastActiveLabel,
  pendingNote,
  rosterSummary,
} from "../careUtils";
import { Plate, SegmentedControl, StatusBadge, Toast, TrendIndicator, icon, useToast } from "../ui";
import { GARDEN_BENCH } from "../art";
import AddPatientModal from "./AddPatientModal";

const FILTERS = [
  ["all", "All"],
  ["active", "Active"],
  ["inactive", "Inactive"],
  ["pending", "Pending"],
];

// Below 900px the cards layout reads better than a sideways-scrolling table.
const NARROW_QUERY = "(max-width: 899px)";

const useNarrow = () => {
  const [narrow, setNarrow] = useState(
    () => !!window.matchMedia && window.matchMedia(NARROW_QUERY).matches,
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(NARROW_QUERY);
    const onChange = () => setNarrow(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return narrow;
};

const subLine = (row) =>
  row.status === "pending"
    ? pendingNote(row)
    : [row.age !== null && row.age !== undefined ? `Age ${row.age}` : null,
       row.connected_since ? `Connected since ${formatDate(row.connected_since)}` : null]
        .filter(Boolean)
        .join(" · ");

const LastActive = ({ row, withNote }) => {
  if (row.status === "pending") {
    return <span className="ct-muted">Not yet connected</span>;
  }
  const flagged = isFlagged(row);
  return (
    <div className="ct-last">
      <span className={flagged ? "ct-flag" : undefined}>
        {flagged && icon(AlertTriangle)}
        {lastActiveLabel(row.days_since_active)}
      </span>
      {flagged && withNote && (
        <span className="ct-last__flag-note">No activity in 7+ days</span>
      )}
    </div>
  );
};

const rowKey = (row) => `${row.kind}-${row.id}`;

const RosterTable = ({ rows, onOpen, onCancel }) => (
  <div className="ct-table-wrap">
    <table className="ct-table" style={{ minWidth: 820 }}>
      <thead>
        <tr>
          <th scope="col">Patient</th>
          <th scope="col">Status</th>
          <th scope="col">Last active</th>
          <th scope="col" className="num">
            Sessions this week
          </th>
          <th scope="col">Trend</th>
          <th scope="col">
            <span className="ct-sr-only">Actions</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const pending = row.status === "pending";
          return (
            <tr
              key={rowKey(row)}
              className={pending ? undefined : "ct-row--open"}
              onClick={pending ? undefined : () => onOpen(row)}
            >
              <td>
                <div className="ct-name-cell">
                  {pending ? (
                    <span className="ct-name">{row.name}</span>
                  ) : (
                    <a
                      href={`/provider/patients/${row.id}`}
                      className="ct-name"
                      style={{ color: "inherit", textDecoration: "none" }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onOpen(row);
                      }}
                    >
                      {row.name}
                    </a>
                  )}
                  <span className="ct-sub">{subLine(row)}</span>
                </div>
              </td>
              <td>
                <StatusBadge status={row.status} />
              </td>
              <td>
                <LastActive row={row} withNote />
              </td>
              <td className="num" style={{ fontSize: 18 }}>
                {pending ? "—" : row.sessions_this_week}
              </td>
              <td>{pending ? <TrendIndicator trend={null} /> : <TrendIndicator trend={row.trend} />}</td>
              <td style={{ textAlign: "right" }}>
                {pending ? (
                  <button
                    type="button"
                    className="ct-btn ct-btn--ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCancel(row);
                    }}
                  >
                    {cancelLabel(row)}
                  </button>
                ) : (
                  <span className="ct-muted" style={{ display: "inline-flex" }}>
                    {icon(ChevronRight)}
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const RosterCards = ({ rows, onOpen, onCancel }) => (
  <div className="ct-cards">
    {rows.map((row) => {
      const pending = row.status === "pending";
      const sessions = row.sessions_this_week;
      return (
        <div
          key={rowKey(row)}
          className={`ct-card ct-patient-card${pending ? " ct-patient-card--pending" : " ct-patient-card--open"}`}
          onClick={pending ? undefined : () => onOpen(row)}
          onKeyDown={
            pending
              ? undefined
              : (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen(row);
                  }
                }
          }
          role={pending ? undefined : "link"}
          tabIndex={pending ? undefined : 0}
          aria-label={pending ? undefined : `Open ${row.name}`}
        >
          <div className="ct-patient-card__head">
            <span className="ct-name" style={{ fontSize: 24 }}>
              {row.name}
            </span>
            <StatusBadge status={row.status} />
          </div>
          <span className="ct-sub">{subLine(row)}</span>
          <hr className="ct-hairline" />
          <div className="ct-patient-card__stats">
            <div>
              <span className="ct-stat-label">Last active</span>
              <LastActive row={row} />
            </div>
            <div>
              <span className="ct-stat-label">This week</span>
              <span className="tnum">
                {pending ? "—" : `${sessions} session${sessions === 1 ? "" : "s"}`}
              </span>
            </div>
          </div>
          <div className="ct-patient-card__foot">
            <TrendIndicator trend={pending ? null : row.trend} />
            {pending && (
              <button
                type="button"
                className="ct-btn ct-btn--ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  onCancel(row);
                }}
              >
                {cancelLabel(row)}
              </button>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

// /provider/patients
const RosterPage = ({ layout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const narrow = useNarrow();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const [toast, showToast] = useToast();
  const greeted = useRef(false);

  const load = useCallback(async () => {
    try {
      setRows(await getRoster());
      setError("");
    } catch (err) {
      setError("We couldn't load your patients. Please refresh the page.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // A toast handed over by the page we came from (sign-up, remove patient).
  useEffect(() => {
    const text = location.state?.toast;
    if (text && !greeted.current) {
      greeted.current = true;
      showToast(text);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate, showToast]);

  const open = (row) => navigate(`/provider/patients/${row.id}`);

  const cancel = async (row) => {
    try {
      if (row.kind === "invite") await cancelInvite(row.id);
      else await cancelRequest(row.id);
      setRows((rs) => rs.filter((r) => rowKey(r) !== rowKey(row)));
      showToast(
        row.kind === "invite"
          ? `Invite for ${row.name} cancelled`
          : `Request to ${row.name} withdrawn`,
      );
    } catch (err) {
      showToast("That didn't work. Please try again.", { error: true });
    }
  };

  const onAdded = (name) => {
    setAdding(false);
    setQuery("");
    setFilter("all");
    showToast(`${name} added as Pending`);
    load();
  };

  const list = rows ? filterRoster(rows, query, filter) : [];
  const useCards = (layout || (narrow ? "cards" : "table")) === "cards";

  return (
    <main className="ct-page">
      <Helmet>
        <title>Patients – MendGrove for clinicians</title>
      </Helmet>
      <div className="ct-title-row">
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <h1>Patients</h1>
          <p className="ct-muted tnum">{(rows && rosterSummary(rows)) || " "}</p>
        </div>
        <button
          type="button"
          className="ct-btn ct-btn--primary ct-btn--lg"
          onClick={() => setAdding(true)}
        >
          {icon(UserPlus)}
          Add patient
        </button>
      </div>

      {error && (
        <p className="ct-form-error" role="alert">
          {icon(AlertTriangle)}
          {error}
        </p>
      )}
      {!rows && !error && <p className="ct-loading">Loading your patients…</p>}

      {rows && rows.length > 0 && (
        <>
          <div className="ct-toolbar">
            <label className="ct-search">
              <span className="ct-search__icon">{icon(Search)}</span>
              <input
                className="ct-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search patients by name"
                aria-label="Search patients"
              />
            </label>
            <SegmentedControl
              label="Filter by activity"
              options={FILTERS}
              value={filter}
              onChange={setFilter}
            />
          </div>
          {list.length === 0 ? (
            <div className="ct-no-results">
              No patients match your search or filter.{" "}
              <button
                type="button"
                className="ct-link-button"
                onClick={() => {
                  setQuery("");
                  setFilter("all");
                }}
              >
                Clear search and filter
              </button>
            </div>
          ) : useCards ? (
            <RosterCards rows={list} onOpen={open} onCancel={cancel} />
          ) : (
            <RosterTable rows={list} onOpen={open} onCancel={cancel} />
          )}
        </>
      )}

      {rows && rows.length === 0 && (
        <div className="ct-empty">
          <Plate
            className="ct-empty__plate"
            label="Illustration: an empty bench beside a garden path"
            art={GARDEN_BENCH}
          />
          <div className="ct-empty__text">
            <h2>No patients yet</h2>
            <p>
              Invite a patient with a link, a QR code or a printed sheet. They'll
              appear here as Pending until they agree to share.
            </p>
            <div className="ct-empty__actions">
              <button
                type="button"
                className="ct-btn ct-btn--primary"
                style={{ minHeight: 48, padding: "0 20px" }}
                onClick={() => setAdding(true)}
              >
                {icon(UserPlus)}
                Add a patient
              </button>
            </div>
          </div>
        </div>
      )}

      {adding && (
        <AddPatientModal
          onClose={() => {
            // An invite may already exist even if the provider closed early.
            setAdding(false);
            load();
          }}
          onDone={onAdded}
        />
      )}
      <Toast toast={toast} />
    </main>
  );
};

export default RosterPage;
